/**
 * S&P RECON-PIPELINE: Client Forensics Tracker & Device Fingerprinting
 * Passive telemetry engine without external dependencies:
 * - Attention Heatmap via IntersectionObserver on critical blocks
 * - Slider interaction telemetry (ValuationMatrix history)
 * - Copy/Paste event interception (Tax/Legal pain marker)
 * - Scroll dynamics & velocity (Scanner vs Analyst)
 * - Hardware & WebGL GPU signature fingerprinting
 */

export interface ForensicsTelemetryState {
  // Session & Timings
  visitorId: string;
  sessionId: string;
  totalTimeSec: number;
  maxScrollPercent: number;
  readingProfile: 'analyst' | 'scanner' | 'rapid_bounce';
  
  // Attention Heatmap (Dwell Timers & Re-reads)
  dwellTimeMatrixSec: number;   // #valuation-matrix
  dwellTimeRealitySec: number;  // #market-reality
  dwellTimeCaseSec: number;     // #case-study
  dwellTimeForkSec: number;     // #strategic-fork / #fork
  reReadMatrix: boolean;
  reReadCase: boolean;
  reReadReality: boolean;
  reReadFork: boolean;
  
  // Slider Telemetry
  sliderTestedValues: number[];
  initialTestedEbitda?: number;
  finalTestedEbitda?: number;
  
  // Copy Intercept
  copiedText?: string;
  copiedKeywordAlarms: string[];
  
  // Device & Environment Fingerprint
  device: {
    platform: string;
    os: string;
    browser: string;
    gpuRenderer: string;
    gpuVendor: string;
    cpuCores: number;
    screenResolution: string;
    viewportSize: string;
    pixelRatio: number;
    colorDepth: number;
    touchPoints: number;
    timezone: string;
    language: string;
    workstationTier: 'Flagship Workstation' | 'Standard Desktop' | 'Mobile/Tablet';
  };
  
  // Marketing & Navigation
  referrer: string;
  referrerDomain: string;
  landingUrl: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  ymClientId?: string;
}

// Global in-memory singleton to hold state across components
class ForensicsTrackerSingleton {
  private startTime = Date.now();
  private maxScroll = 0;
  private scrollEntriesCount = 0;
  private firstScrollTimestamp = 0;
  
  private dwellTimers: Record<string, number> = {
    'valuation-matrix': 0,
    'market-reality': 0,
    'case-study': 0,
    'strategic-fork': 0,
    'fork': 0
  };
  
  private activeIntervals: Record<string, any> = {};
  private pastPositions: Record<string, number> = {};
  private reReadFlags: Record<string, boolean> = {
    'valuation-matrix': false,
    'market-reality': false,
    'case-study': false,
    'strategic-fork': false
  };
  
  private sliderValues: number[] = [];
  private copiedSnippets: string[] = [];
  private copiedAlarms: Set<string> = new Set();
  
  private isInitialized = false;

  public init() {
    if (typeof window === 'undefined' || this.isInitialized) return;
    this.isInitialized = true;
    this.startTime = Date.now();

    // 1. Scroll tracking & velocity analysis
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // 2. Copy/Paste intercept listener
    document.addEventListener('copy', this.handleCopy);

    // 3. Setup IntersectionObserver for attention heatmap
    this.setupIntersectionObservers();
  }

  private handleScroll = () => {
    if (typeof window === 'undefined') return;
    const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (totalHeight > 0) {
      const currentScroll = Math.min(100, Math.round((window.scrollY / totalHeight) * 100));
      if (currentScroll > this.maxScroll) {
        this.maxScroll = currentScroll;
      }
      if (!this.firstScrollTimestamp && currentScroll > 15) {
        this.firstScrollTimestamp = Date.now();
      }
      this.scrollEntriesCount++;
    }
  };

  private handleCopy = () => {
    try {
      const selection = window.getSelection()?.toString()?.trim();
      if (selection && selection.length > 0) {
        const cleaned = selection.substring(0, 150);
        this.copiedSnippets.push(cleaned);
        
        // Scan for known legal/tax anxiety keywords
        const keywords = [
          '54.1', 'НК РФ', '406.1', 'ГК РФ', '238', '238.1', 'УК РФ',
          'Change of Control', 'WIP', 'NWC', 'EBITDA', 'QoE', 'Add-backs',
          'дроблен', 'эскроу', 'Escrow', 'амнисти', '176-ФЗ', 'Росздравнадзор',
          'БТИ', 'ЕГИСЗ', 'Double Dipping', 'Rollover', 'Asset Deal', 'Share Deal'
        ];
        
        keywords.forEach(kw => {
          if (selection.toLowerCase().includes(kw.toLowerCase())) {
            this.copiedAlarms.add(kw);
          }
        });
      }
    } catch {
      // Safe fallback
    }
  };

  private setupIntersectionObservers() {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const targetIds = ['valuation-matrix', 'market-reality', 'case-study', 'strategic-fork', 'fork'];

    // Poll for DOM elements if not yet mounted
    const checkElements = () => {
      targetIds.forEach(id => {
        const el = document.getElementById(id);
        if (el && !(el as any).__sp_tracked) {
          (el as any).__sp_tracked = true;
          this.observeSection(el, id);
        }
      });
    };

    setTimeout(checkElements, 500);
    setTimeout(checkElements, 2000);
  }

  private observeSection(element: HTMLElement, sectionId: string) {
    let timerPending: any = null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const canonicalId = sectionId === 'fork' ? 'strategic-fork' : sectionId;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
            // Check if this is a re-read (user scrolled down past element and returned)
            const boundingTop = entry.boundingClientRect.top;
            if (this.pastPositions[canonicalId] !== undefined) {
              if (this.pastPositions[canonicalId] < -100 && boundingTop >= -50) {
                this.reReadFlags[canonicalId] = true;
              }
            }
            this.pastPositions[canonicalId] = boundingTop;

            // Wait 3 seconds of sustained visibility before ticking dwell timer
            if (!timerPending && !this.activeIntervals[canonicalId]) {
              timerPending = setTimeout(() => {
                this.activeIntervals[canonicalId] = setInterval(() => {
                  this.dwellTimers[canonicalId] = (this.dwellTimers[canonicalId] || 0) + 1;
                }, 1000);
              }, 3000);
            }
          } else {
            // Stopped viewing
            if (timerPending) {
              clearTimeout(timerPending);
              timerPending = null;
            }
            if (this.activeIntervals[canonicalId]) {
              clearInterval(this.activeIntervals[canonicalId]);
              delete this.activeIntervals[canonicalId];
            }
            this.pastPositions[canonicalId] = entry.boundingClientRect.top;
          }
        });
      },
      { threshold: [0, 0.45, 0.75, 1.0] }
    );

    observer.observe(element);
  }

  public recordSliderChange(nameOrVal: string | number, maybeVal?: number) {
    const val = typeof nameOrVal === 'number' ? nameOrVal : (typeof maybeVal === 'number' ? maybeVal : 0);
    if (this.sliderValues[this.sliderValues.length - 1] !== val) {
      this.sliderValues.push(val);
      if (this.sliderValues.length > 20) {
        this.sliderValues = this.sliderValues.slice(-20);
      }
    }
  }

  public getSnapshot(): ForensicsTelemetryState {
    const durationSec = Math.floor((Date.now() - this.startTime) / 1000);
    
    // Determine reader profile
    let profile: 'analyst' | 'scanner' | 'rapid_bounce' = 'analyst';
    if (durationSec < 15 && this.maxScroll > 60) {
      profile = 'scanner';
    } else if (durationSec < 8 && this.maxScroll < 20) {
      profile = 'rapid_bounce';
    }

    const device = this.collectDeviceFingerprint();
    const q = this.getQueryParams();
    const rawReferrer = typeof document !== 'undefined' ? document.referrer : '';
    let refDomain = 'Direct';
    if (rawReferrer) {
      try {
        refDomain = new URL(rawReferrer).hostname;
      } catch {
        refDomain = rawReferrer;
      }
    }

    return {
      visitorId: this.getVisitorId(),
      sessionId: this.getSessionId(),
      totalTimeSec: durationSec,
      maxScrollPercent: this.maxScroll,
      readingProfile: profile,
      dwellTimeMatrixSec: this.dwellTimers['valuation-matrix'] || 0,
      dwellTimeRealitySec: this.dwellTimers['market-reality'] || 0,
      dwellTimeCaseSec: this.dwellTimers['case-study'] || 0,
      dwellTimeForkSec: (this.dwellTimers['strategic-fork'] || 0) + (this.dwellTimers['fork'] || 0),
      reReadMatrix: !!this.reReadFlags['valuation-matrix'],
      reReadCase: !!this.reReadFlags['case-study'],
      reReadReality: !!this.reReadFlags['market-reality'],
      reReadFork: !!this.reReadFlags['strategic-fork'],
      sliderTestedValues: [...this.sliderValues],
      initialTestedEbitda: this.sliderValues.length > 0 ? this.sliderValues[0] : undefined,
      finalTestedEbitda: this.sliderValues.length > 0 ? this.sliderValues[this.sliderValues.length - 1] : undefined,
      copiedText: this.copiedSnippets.length > 0 ? this.copiedSnippets[this.copiedSnippets.length - 1] : undefined,
      copiedKeywordAlarms: Array.from(this.copiedAlarms),
      device,
      referrer: rawReferrer || 'Прямой заход (Direct)',
      referrerDomain: refDomain,
      landingUrl: typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '',
      utmSource: q['utm_source'] || q['source'],
      utmMedium: q['utm_medium'],
      utmCampaign: q['utm_campaign'],
      utmTerm: q['utm_term'],
      utmContent: q['utm_content'],
      ymClientId: this.getCookie('_ym_uid')
    };
  }

  private collectDeviceFingerprint() {
    if (typeof window === 'undefined') {
      return {
        platform: 'SSR',
        os: 'Server',
        browser: 'SSR',
        gpuRenderer: 'Unknown',
        gpuVendor: 'Unknown',
        cpuCores: 4,
        screenResolution: '0x0',
        viewportSize: '0x0',
        pixelRatio: 1,
        colorDepth: 24,
        touchPoints: 0,
        timezone: 'Europe/Moscow',
        language: 'ru',
        workstationTier: 'Standard Desktop' as const
      };
    }

    const ua = navigator.userAgent;
    const cpuCores = navigator.hardwareConcurrency || 4;
    const touchPoints = navigator.maxTouchPoints || 0;
    const pixelRatio = window.devicePixelRatio || 1;
    const screenRes = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow';
    const lang = navigator.language || 'ru';

    // WebGL GPU Renderer Extraction
    const { gpuRenderer, gpuVendor } = this.getGpuInfo();

    // Determine Workstation Tier
    let workstationTier: 'Flagship Workstation' | 'Standard Desktop' | 'Mobile/Tablet' = 'Standard Desktop';
    const isAppleSilicon = /Apple M[1-9]/i.test(gpuRenderer) || (navigator.platform === 'MacIntel' && touchPoints > 0) || /Macintosh/i.test(ua);
    const isHighEndGpu = /RTX|Radeon RX|Quadro|Apple M/i.test(gpuRenderer);
    const isMobile = /(mobile|iphone|android|ipad)/i.test(ua) || (window.innerWidth < 768);

    if (isMobile) {
      workstationTier = 'Mobile/Tablet';
    } else if (isAppleSilicon || (isHighEndGpu && cpuCores >= 8)) {
      workstationTier = 'Flagship Workstation';
    }

    return {
      platform: navigator.platform || 'Unknown',
      os: this.detectOS(ua),
      browser: this.detectBrowser(ua),
      gpuRenderer,
      gpuVendor,
      cpuCores,
      screenResolution: `${screenRes} @ ${pixelRatio}x`,
      viewportSize: viewport,
      pixelRatio,
      colorDepth: window.screen.colorDepth || 24,
      touchPoints,
      timezone: tz,
      language: lang,
      workstationTier
    };
  }

  private getGpuInfo(): { gpuRenderer: string; gpuVendor: string } {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        const debugInfo = (gl as any).getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = (gl as any).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'Generic GPU';
          const vendor = (gl as any).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'Generic Vendor';
          return {
            gpuRenderer: String(renderer).replace(/ANGLE \((.*)\)/, '$1'),
            gpuVendor: String(vendor)
          };
        }
      }
    } catch {
      // Fallback
    }
    return { gpuRenderer: 'Hardware Accelerated (WebGL)', gpuVendor: 'Standard' };
  }

  private detectOS(ua: string): string {
    if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac OS X/.test(ua)) return 'macOS';
    if (/Windows NT 10.0|Windows NT 11.0/.test(ua)) return 'Windows 10/11';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Unknown OS';
  }

  private detectBrowser(ua: string): string {
    if (/YaBrowser/i.test(ua)) return 'Yandex Browser';
    if (/Edg/i.test(ua)) return 'Microsoft Edge';
    if (/Chrome/i.test(ua) && !/Chromium|OPR|Edg/i.test(ua)) return 'Google Chrome';
    if (/Safari/i.test(ua) && !/Chrome|Chromium|Android/i.test(ua)) return 'Apple Safari';
    if (/Firefox/i.test(ua)) return 'Mozilla Firefox';
    if (/Opera|OPR/i.test(ua)) return 'Opera';
    return 'Other Browser';
  }

  private getQueryParams(): Record<string, string> {
    if (typeof window === 'undefined') return {};
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((val, key) => {
      params[key] = val;
    });
    return params;
  }

  private getCookie(name: string): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : undefined;
  }

  private getVisitorId(): string {
    if (typeof window === 'undefined') return 'SP-SSR';
    try {
      let vid = localStorage.getItem('sp_recon_uid');
      if (!vid) {
        vid = 'SP-RECON-' + Math.random().toString(36).substring(2, 8).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
        localStorage.setItem('sp_recon_uid', vid);
      }
      return vid;
    } catch {
      return 'SP-EPHEMERAL-' + Date.now();
    }
  }

  private getSessionId(): string {
    if (typeof window === 'undefined') return 'SP-SES-SSR';
    try {
      let sid = sessionStorage.getItem('sp_recon_ses');
      if (!sid) {
        sid = 'SES-' + Math.random().toString(36).substring(2, 7).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
        sessionStorage.setItem('sp_recon_ses', sid);
      }
      return sid;
    } catch {
      return 'SES-RAM-ONLY';
    }
  }
}

export const forensicsTracker = new ForensicsTrackerSingleton();

// Hook for React components
export function useForensicsTracker() {
  if (typeof window !== 'undefined') {
    forensicsTracker.init();
  }

  return {
    recordSliderChange: (nameOrVal: string | number, maybeVal?: number) => forensicsTracker.recordSliderChange(nameOrVal, maybeVal),
    getTelemetrySnapshot: () => forensicsTracker.getSnapshot()
  };
}
